import { useCallback, useEffect, useState } from 'react'

import axios from 'axios'

import useSentry from 'Pods/Shared/Hooks/useSentry'

const LOGIN_PATH = '/users/2fa-or-login.json'
// Expected auth outcomes; only other statuses are worth a Sentry event.
const EXPECTED_FAILURE_STATUSES = [401, 429]

// MTCaptcha readiness lives in window globals set by Utils/MTCaptcha.jsx
// callbacks; a widget that never rendered must not block submission.
function captchaIsValid() {
  const captchaIsFillable = window.renderedcb && window.jsloadedcb
  return captchaIsFillable ? window.verifiedcb : true
}

function firstError(errors) {
  return (Array.isArray(errors) ? errors[0] : errors) || null
}

// Password login against /users/2fa-or-login.json for plain-useState flows
// (AuthModal, AiSignInModal). Unlike useLogin it has no react-hook-form or
// flash-event coupling, but keeps the endpoint's quirks: the MTCaptcha token
// nested under user params (VerifyMTCaptcha reads it from there), the 2FA
// second step and the security-tool HTML interstitial. Callers own i18n -
// onError receives message: null when only a generic fallback applies.
// Uses axios directly (not useApiPost): the request body carries credentials,
// and useApiPost forwards the raw Axios error - config.data included - to
// Sentry on failure.
function usePasswordLogin({ captchaId }) {
  const { captureException } = useSentry()
  const [isLoading, setIsLoading] = useState(false)
  // Set post-mount (not during render) so SSR markup and hydration agree.
  const [captchaEnabled, setCaptchaEnabled] = useState(false)

  useEffect(() => {
    setCaptchaEnabled(Boolean(window.gon?.features?.lcaptcha || window.pageConfig?.features?.lcaptcha))
  }, [])

  const submitLogin = useCallback(({
    email, password, twoFaCode, onSuccess, onTwoFaRequired, onError,
  }) => {
    if (!captchaIsValid()) return

    const resetCaptcha = () => {
      if (captchaEnabled) window.mtcaptcha?.resetUI(captchaId)
    }

    const captchaToken = captchaEnabled ? window.mtcaptcha?.getVerifiedToken(captchaId) : undefined
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    setIsLoading(true)
    axios.post(LOGIN_PATH, {
      user: {
        login: email,
        password,
        remember_me: 1,
        ...(captchaEnabled && { MTCaptchaToken: captchaToken }),
      },
      ...(captchaEnabled && { MTCaptchaToken: captchaToken }),
      ...(twoFaCode && { code: twoFaCode }),
      location: window.location.pathname,
    }, {
      headers: { 'X-CSRF-Token': csrfToken },
      withCredentials: true,
    })
      .then(({ data }) => {
        setIsLoading(false)

        if (data?.twoFaEnabled) {
          onTwoFaRequired()
          return
        }

        // Security tool asks for verification - render its HTML as-is.
        if (typeof data === 'string') {
          document.write(data)
          return
        }

        if (data?.success === false) {
          resetCaptcha()
          onError({ message: firstError(data.errors) })
          return
        }

        onSuccess({ redirectTo: data?.redirectTo })
      })
      .catch((error) => {
        setIsLoading(false)
        const status = error?.response?.status

        // Never capture the Axios error itself: config.data holds the
        // submitted credentials. Report only the response status.
        if (!EXPECTED_FAILURE_STATUSES.includes(status)) {
          captureException(new Error('Password login submission failed'), {
            tags: { section: 'React login' },
            extra: { status },
          })
        }

        resetCaptcha()
        onError({ message: status === 429 ? null : firstError(error?.response?.data?.errors) })
      })
  }, [captchaEnabled, captchaId, captureException])

  return { submitLogin, isLoading, captchaEnabled }
}

export default usePasswordLogin
